// useOverlayBackGuard — аппаратная кнопка «Назад» в Capacitor сначала закрывает
// открытый попап, а уже потом уходит назад/выходит из приложения.
//
// ВАЖНО (router-safety): здесь НЕТ ручного `window.history.pushState`. Раньше для
// перехвата browser-back в мобильном вебе пушилась «сторожевая» запись
// `pushState({__overlayGuard:true}, "")` — но это ЗАТИРАЛО state-объект, в котором
// TanStack Router хранит свой индекс истории. После такого пуша любая навигация по
// ссылке из шторки/меню (например профиль → /settings/profile) роняла роутер на
// главную. Поэтому ручную манипуляцию историей убрали полностью.
//
// В Capacitor аппаратный back приходит событием `App.backButton` — его можно
// обработать напрямую, без записей в history:
//   • открыт оверлей → закрываем его (dispatch Escape — Radix DismissableLayer /
//     vaul dismissible слушают keydown), навигацию не трогаем;
//   • иначе → шаг назад по истории (router сам отработает popstate), а в корне —
//     выход из приложения.
//
// Мобильный веб (браузерная кнопка/жест «назад») не перехватываем: сделать это
// можно только через запись в history, что несовместимо с TanStack Router. При
// необходимости вернёмся к router-интегрированному варианту.
//
// Полноэкранные режимы (запись лекции, голосовой звонок) оверлеями НЕ являются —
// разметочного признака у них нет и быть не должно. Они заявляют своё поведение
// «Назад» явно, через `useBackInterceptor` (обоснование — `lib/back-interceptors.ts`).
//
// Воркспейс курса (§7.4 плана V2) опирается ровно на это поведение: открытая
// шторка «Темы / Источники / Инструменты / Репетитор» — это vaul-drawer, то есть
// обычный оверлей; первый back её закрывает, второй уходит по истории (то есть
// из курса). Своего перехвата воркспейс НЕ заводит и `pushState` не трогает.

import { useEffect } from "react";
import { runBackInterceptors } from "@/lib/back-interceptors";
import { IS_CAPACITOR } from "@/lib/platform-detect";

// Что считаем «открытым оверлеем». Кроме Radix Dialog/AlertDialog сюда входят:
//   • `[data-vaul-drawer]` — шторки (BottomSheet / ActionMenu-на-touch). Vaul
//     рендерит Radix-диалог, но атрибуты держит на своём узле: селектор по
//     drawer'у не даёт промаха, если разметка примитива изменится;
//   • `[role="menu"]` — открытое DropdownMenu (`⋯` строк списков): системная
//     «назад» обязана сначала закрыть меню, а не уводить с экрана.
//
// Оверлей не обязан быть Radix-компонентом: `AssetLightbox` (полноэкранный
// просмотр вложений) сознательно сделан порталом со своими pointer-жестами и
// заявляет себя тем же разметочным признаком `role="dialog"` + `data-state`.
const OVERLAY_SELECTOR = [
	'[role="dialog"][data-state="open"]',
	'[role="alertdialog"][data-state="open"]',
	"[data-vaul-drawer][data-state='open']",
	'[role="menu"][data-state="open"]',
].join(", ");

/**
 * Верхний открытый оверлей — ПОСЛЕДНИЙ в порядке документа: и Radix, и vaul, и
 * лайтбокс портируются в конец `body`, поэтому позже открытый лежит ниже по DOM
 * и выше по экрану.
 */
function topOverlay(): HTMLElement | null {
	const open = document.querySelectorAll<HTMLElement>(OVERLAY_SELECTOR);
	// `NodeListOf.item()` типизирован как non-null, хотя на пустом списке вернёт
	// null — индексируем явно, чтобы тип не врал.
	return open[open.length - 1] ?? null;
}

function closeOverlay(overlay: HTMLElement): void {
	// Escape закрывает верхний Radix Dialog / vaul Drawer (DismissableLayer слушает
	// keydown на document; vaul dismissible=true по умолчанию).
	//
	// Шлём событие В САМ оверлей, а не в `document`: цель события — тот, кого
	// просим закрыться, а до слушателей Radix/vaul оно всё равно доходит (они
	// висят на `document` в фазе перехвата, то есть ДО цели).
	//
	// Из этого же следует, что не-Radix оверлей (лайтбокс поверх мобильной
	// шторки репетитора) обязан перехватывать Escape на `window` в фазе
	// перехвата и глушить всплытие — иначе Radix посчитал бы верхним слоем
	// шторку под ним и закрыл её вместе с просмотром. См. `asset-lightbox.tsx`.
	overlay.dispatchEvent(
		new KeyboardEvent("keydown", {
			key: "Escape",
			code: "Escape",
			bubbles: true,
			cancelable: true,
		}),
	);
}

interface PluginListenerHandle {
	remove: () => Promise<void> | void;
}

interface CapacitorAppPlugin {
	// В одних сборках Capacitor addListener возвращает Promise<handle>, в других —
	// handle синхронно. Тип покрывает оба варианта, вызов нормализуем Promise.resolve.
	addListener(
		event: "backButton",
		cb: (data: { canGoBack: boolean }) => void,
	): Promise<PluginListenerHandle> | PluginListenerHandle;
	exitApp(): Promise<void>;
}

function getCapacitorApp(): CapacitorAppPlugin | null {
	const cap = (window as unknown as { Capacitor?: { Plugins?: { App?: CapacitorAppPlugin } } })
		.Capacitor;
	return cap?.Plugins?.App ?? null;
}

export function useOverlayBackGuard(): void {
	useEffect(() => {
		if (!IS_CAPACITOR) return;
		const app = getCapacitorApp();
		if (!app) return;

		let removeCap: (() => void) | undefined;
		try {
			// addListener может вернуть и Promise<handle>, и handle синхронно —
			// Promise.resolve нормализует оба случая (иначе `.then` на sync-handle
			// бросал «then is not a function» и валил всё приложение).
			const maybeHandle = app.addListener("backButton", ({ canGoBack }) => {
				// 1. Оверлей поверх всего — закрываем его. Диалог, открытый ПОВЕРХ
				//    полноэкранного экрана, обязан закрыться раньше самого экрана.
				const overlay = topOverlay();
				if (overlay) {
					closeOverlay(overlay);
					return;
				}
				// 2. Полноэкранный режим заявил своё поведение «Назад» (свернуть
				//    запись, спросить о завершении звонка) — lib/back-interceptors.
				if (runBackInterceptors()) return;
				// 3. Обычный шаг назад по истории, в корне — выход из приложения.
				if (canGoBack) window.history.back();
				else void app.exitApp();
			});
			void Promise.resolve(maybeHandle)
				.then((handle) => {
					removeCap = () => void handle?.remove?.();
				})
				.catch(() => undefined);
		} catch {
			// back-button — прогрессивное улучшение; сбой не должен ломать монтирование
		}

		return () => {
			removeCap?.();
		};
	}, []);
}
